
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { ArrowLeft, LockIcon, UserIcon, Mail, Phone } from 'lucide-react';
import { loginTemplates, LoginTemplate } from '@/lib/loginTemplates';
import { toast } from 'sonner';
import { cn } from '@/lib/utils';

const SignUpPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();

  const [template, setTemplate] = useState<LoginTemplate | null>(null);
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isMounted, setIsMounted] = useState(false);

  useEffect(() => {
    const found = loginTemplates.find(t => t.id === id);
    if (found) {
      setTemplate(found);
      document.body.style.backgroundColor = found.backgroundColor;
      document.body.style.color = ['dota2', 'discord', 'steam'].includes(found.id) ? '#FFFFFF' : '';
    } else {
      navigate('/');
      toast.error('Sign up template not found');
    }
    
    return () => {
      document.body.style.backgroundColor = '';
      document.body.style.color = '';
    };
  }, [id, navigate]);

  useEffect(() => {
    setIsMounted(true);
    return () => setIsMounted(false);
  }, []);

  if (!template) {
    return <div className="min-h-screen flex items-center justify-center">Loading...</div>;
  }

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    if (password !== confirmPassword) {
      toast.error('Passwords do not match');
      return;
    }
    
    setIsLoading(true);
    
    // Simulate a sign up attempt
    setTimeout(() => {
      toast.success('Account creation simulated');
      navigate(`/login/${id}`);
      setIsLoading(false);
    }, 1500);
  };

  const Icon = template.icon;
  const isDark = ['dota2', 'mobilelegends', 'discord', 'steam'].includes(template.id);

  // Custom styling based on service
  const getCustomStyles = () => {
    switch(template.id) {
      case 'facebook':
        return {
          inputClass: "rounded-md",
          buttonClass: "rounded-md font-bold",
          logoClass: "h-16 mb-4",
          containerClass: "max-w-md"
        };
      case 'instagram':
        return {
          inputClass: "rounded border-gray-300",
          buttonClass: "rounded-md font-semibold",
          logoClass: "h-16 mb-4",
          containerClass: "max-w-sm"
        };
      default:
        return {
          inputClass: "",
          buttonClass: "",
          logoClass: "h-12 mb-4",
          containerClass: "max-w-md"
        };
    }
  };

  const customStyles = getCustomStyles();

  return (
    <div 
      className={cn(
        "min-h-screen flex flex-col items-center justify-center px-6 py-12 transition-all duration-500",
        isMounted ? "opacity-100" : "opacity-0",
        isDark ? "text-white" : ""
      )}
      style={{ backgroundColor: template.backgroundColor }}
    >
      <div className="absolute top-4 left-4 flex space-x-4">
        <Button
          variant="ghost"
          size="icon"
          onClick={() => navigate(`/login/${id}`)}
          className={cn(
            "rounded-full",
            isDark ? "text-white hover:bg-white/10" : "text-foreground hover:bg-background/90"
          )}
        >
          <ArrowLeft className="h-5 w-5" />
          <span className="sr-only">Go back to login</span>
        </Button>
      </div>
      
      <div className={cn(
        "w-full p-8 rounded-2xl animate-scale-in",
        customStyles.containerClass,
        isDark ? "glass-dark" : "glass-card"
      )}>
        <div className="flex flex-col items-center mb-6">
          {template.logoUrl ? (
            <img 
              src={template.logoUrl} 
              alt={template.name} 
              className={cn("object-contain", customStyles.logoClass)}
            />
          ) : (
            <div 
              className="w-16 h-16 rounded-xl flex items-center justify-center text-white mb-4"
              style={{ backgroundColor: template.primaryColor }}
            >
              <Icon className="w-8 h-8" />
            </div>
          )}
          
          <h2 className="text-2xl font-semibold">Create {template.name} Account</h2>
          
          <p className={cn(
            "text-sm mt-2 text-center",
            isDark ? "text-gray-300" : "text-muted-foreground"
          )}>
            Join millions of users on {template.name}
          </p>
        </div>
        
        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label 
              htmlFor="fullName" 
              className={isDark ? "text-gray-300" : ""}
            >
              Full Name
            </Label>
            <div className="relative">
              <Input
                id="fullName"
                type="text"
                value={fullName}
                onChange={(e) => setFullName(e.target.value)}
                placeholder="Enter your full name"
                className={cn(
                  "pl-10",
                  customStyles.inputClass,
                  isDark 
                    ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                    : "bg-white"
                )}
                required
              />
              <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label 
              htmlFor="email" 
              className={isDark ? "text-gray-300" : ""}
            >
              Email Address
            </Label>
            <div className="relative">
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="Enter your email address"
                className={cn(
                  "pl-10",
                  customStyles.inputClass,
                  isDark 
                    ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                    : "bg-white"
                )}
                required
              />
              <Mail className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label 
              htmlFor="phone" 
              className={isDark ? "text-gray-300" : ""}
            >
              Phone Number
            </Label>
            <div className="relative">
              <Input
                id="phone"
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder="Enter your phone number"
                className={cn(
                  "pl-10",
                  customStyles.inputClass,
                  isDark 
                    ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                    : "bg-white"
                )}
              />
              <Phone className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          
          {["github", "reddit", "twitter", "twitch"].includes(template.id) && (
            <div className="space-y-2">
              <Label 
                htmlFor="username" 
                className={isDark ? "text-gray-300" : ""}
              >
                Username
              </Label>
              <div className="relative">
                <Input
                  id="username"
                  type="text"
                  value={username}
                  onChange={(e) => setUsername(e.target.value)}
                  placeholder="Choose a username"
                  className={cn(
                    "pl-10",
                    customStyles.inputClass,
                    isDark 
                      ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                      : "bg-white"
                  )}
                  required
                />
                <UserIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              </div>
            </div>
          )}
          
          <div className="space-y-2">
            <Label 
              htmlFor="password"
              className={isDark ? "text-gray-300" : ""}
            >
              Password
            </Label>
            <div className="relative">
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Create a password"
                className={cn(
                  "pl-10",
                  customStyles.inputClass,
                  isDark 
                    ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                    : "bg-white"
                )}
                required
              />
              <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          
          <div className="space-y-2">
            <Label 
              htmlFor="confirmPassword"
              className={isDark ? "text-gray-300" : ""}
            >
              Confirm Password
            </Label>
            <div className="relative">
              <Input
                id="confirmPassword"
                type="password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Confirm your password"
                className={cn(
                  "pl-10",
                  customStyles.inputClass,
                  isDark 
                    ? "bg-white/10 border-white/20 text-white placeholder:text-gray-400" 
                    : "bg-white"
                )}
                required
              />
              <LockIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            </div>
          </div>
          
          <Button
            type="submit"
            className={cn(
              "w-full font-medium h-11 transition-all duration-300 mt-2",
              customStyles.buttonClass
            )}
            style={{ 
              backgroundColor: template.primaryColor,
              color: template.secondaryColor,
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="h-4 w-4 border-t-2 border-b-2 border-current rounded-full animate-spin"></div>
                <span>Processing...</span>
              </div>
            ) : (
              "Sign Up"
            )}
          </Button>
          
          <div className={cn(
            "text-center mt-6 text-sm",
            isDark ? "text-gray-300" : "text-muted-foreground"
          )}>
            Already have an account?{" "}
            <button 
              type="button"
              onClick={() => navigate(`/login/${id}`)}
              className="font-medium"
              style={{ color: template.primaryColor }}
            >
              Sign In
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default SignUpPage;